import React, { useState, useEffect, useMemo, useContext } from "react";
import { Modal } from "react-bootstrap";
import Link from "../TrailingSlashLink";
import Logo from "../Logo";
import GlobalContext from "../../context/GlobalContext";
import { isViewGloballyDisabled } from "../../utils/planUtils";

/** When plan lacks a view but user may open dashboard, route to page; PageAccessGate applies blur. */
const LOCKED_NAV_ROUTES_IF_CAN_ACCESS_DASHBOARD = new Set([
  "tender",
  "result",
  "boq-lead",
  "tender360-dashboard",
  "tender360-bidder-profile",
  "tender360-competitor-matrix",
  "tender360-missed-opportunities",
  "tender360-lost-tender-evaluation",
  "tender360-rate-analysis",
  "tender360-department-insights",
  "tender360-state-statistics",
  "tender360-analytical-report",
  "calendar",
  "gem-registration",
  "apex-expert-connect",
]);

const SidebarDashboard2 = ({
  showSidebar,
  activeView,
  onViewChange,
  visibleSidebarItems,
  canAccessDashboard,
  isAuthenticated = false,
}) => {
  const gContext = useContext(GlobalContext);
  const [isTender360Expanded, setIsTender360Expanded] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const canAccessView = useMemo(() => {
    const set = visibleSidebarItems;
    if (!set || typeof set.has !== "function") return () => false;
    if (typeof set.size === "number" && set.size === 0) return () => false;
    return (viewId) =>
      !isViewGloballyDisabled(viewId) && set.has(viewId);
  }, [visibleSidebarItems]);

  const lockedNavStyle = (viewId) =>
    !canAccessView(viewId)
      ? { filter: "blur(3px)", opacity: 0.88 }
      : undefined;

  useEffect(() => {
    if (activeView && String(activeView).startsWith("tender360-")) {
      setIsTender360Expanded(true);
    }
  }, [activeView]);

  const handleTender360Click = (e) => {
    e.preventDefault();
    setIsTender360Expanded(!isTender360Expanded);
  };

  const handleMenuItemClick = (view) => {
    if (canAccessView(view)) {
      onViewChange(view);
      return;
    }
    if (view === "tender360-bidder-profile") {
      onViewChange(view);
      return;
    }
    if (
      isAuthenticated &&
      canAccessDashboard &&
      LOCKED_NAV_ROUTES_IF_CAN_ACCESS_DASHBOARD.has(view)
    ) {
      onViewChange(view);
      return;
    }
    setShowUpgradeModal(true);
  };

  return (
    <div className={`dashboard-sidebar-wrapper pt-11 d-flex flex-column border-right${showSidebar ? " show" : ""}`} id="sidebar">
      <div className="brand-logo px-11 mt-7 mb-15">
        <Logo height="120px" />
      </div>
      <div className="overflow-auto flex-grow-1">
        <ul className="list-unstyled dashboard-layout-sidebar">
        {/* Tender */}
        <li>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleMenuItemClick("tender");
            }}
            style={lockedNavStyle("tender")}
            className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
              activeView === "tender" ? "active" : ""
            }`}
          >
            <span>Tender</span>
          </a>
        </li>
        <li>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleMenuItemClick("result");
            }}
            style={lockedNavStyle("result")}
            className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
              activeView === "result" ? "active" : ""
            }`}
          >
            <span>Result</span>
          </a>
        </li>
        <li>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleMenuItemClick("boq-lead");
            }}
            style={lockedNavStyle("boq-lead")}
            className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
              activeView === "boq-lead" ? "active" : ""
            }`}
          >
            <span>BOQ Lead</span>
          </a>
        </li>

        {/* Tender 360 - Expandable */}
        <li>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleTender360Click(e);
            }}
            style={lockedNavStyle("tender360-dashboard")}
            className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
              activeView?.startsWith("tender360-") ? "active" : ""
            }`}
          >
            <span>Tender 360</span>
            <i className={`fas fa-chevron-${isTender360Expanded ? 'down' : 'right'} ml-auto`}></i>
          </a>
          
          {/* Tender 360 Sub-menu */}
          {isTender360Expanded && (
            <ul className="list-unstyled pl-11">
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuItemClick("tender360-dashboard");
                  }}
                  style={lockedNavStyle("tender360-dashboard")}
                  className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
                    activeView === "tender360-dashboard" ? "active" : ""
                  }`}
                >
                  <span>Dashboard</span>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuItemClick("tender360-bidder-profile");
                  }}
                  style={lockedNavStyle("tender360-bidder-profile")}
                  className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
                    activeView === "tender360-bidder-profile" ? "active" : ""
                  }`}
                >
                  <span>Bidder Profile</span>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuItemClick("tender360-competitor-matrix");
                  }}
                  style={lockedNavStyle("tender360-competitor-matrix")}
                  className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
                    activeView === "tender360-competitor-matrix" ? "active" : ""
                  }`}
                >
                  <span>Competitor Matrix</span>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuItemClick("tender360-missed-opportunities");
                  }}
                  style={lockedNavStyle("tender360-missed-opportunities")}
                  className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
                    activeView === "tender360-missed-opportunities" ? "active" : ""
                  }`}
                >
                  <span>Missed Opportunities</span>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuItemClick("tender360-lost-tender-evaluation");
                  }}
                  style={lockedNavStyle("tender360-lost-tender-evaluation")}
                  className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
                    activeView === "tender360-lost-tender-evaluation" ? "active" : ""
                  }`}
                >
                  <span>Lost Tender Evaluation</span>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuItemClick("tender360-rate-analysis");
                  }}
                  style={lockedNavStyle("tender360-rate-analysis")}
                  className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
                    activeView === "tender360-rate-analysis" ? "active" : ""
                  }`}
                >
                  <span>Rate Analysis</span>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuItemClick("tender360-department-insights");
                  }}
                  style={lockedNavStyle("tender360-department-insights")}
                  className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
                    activeView === "tender360-department-insights" ? "active" : ""
                  }`}
                >
                  <span>Department Insights</span>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuItemClick("tender360-state-statistics");
                  }}
                  style={lockedNavStyle("tender360-state-statistics")}
                  className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
                    activeView === "tender360-state-statistics" ? "active" : ""
                  }`}
                >
                  <span>State Statistics</span>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMenuItemClick("tender360-analytical-report");
                  }}
                  style={lockedNavStyle("tender360-analytical-report")}
                  className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
                    activeView === "tender360-analytical-report" ? "active" : ""
                  }`}
                >
                  <span>Analytical Report</span>
                </a>
              </li>
            </ul>
          )}
        </li>

        {/* Other Main Menu Items */}
        <li>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleMenuItemClick("tender-bidding");
            }}
            style={lockedNavStyle("tender-bidding")}
            className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
              activeView === "tender-bidding" ? "active" : ""
            }`}
          >
            <span>Tender Bidding</span>
          </a>
        </li>
        <li>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handleMenuItemClick("calendar");
            }}
            style={lockedNavStyle("calendar")}
            className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
              activeView === "calendar" ? "active" : ""
            }`}
          >
            <span>Calendar</span>
          </a>
        </li>
        <li>
          <a
            href="/contact"
            onClick={(e) => {
              e.preventDefault();
              handleMenuItemClick("apex-expert-connect");
            }}
            style={lockedNavStyle("apex-expert-connect")}
            className={`px-10 py-3 d-flex align-items-center font-size-4 font-weight-semibold ${
              activeView === "apex-expert-connect" ? "active" : ""
            }`}
          >
            <span>Apex Expert Connect</span>
          </a>
        </li>
      </ul>

      <style>{`.upgrade-modal .modal-content { border-radius: 3px !important; } .upgrade-modal-btn { border-radius: 3px !important; }`}</style>
      <Modal show={showUpgradeModal} onHide={() => setShowUpgradeModal(false)} centered dialogClassName="upgrade-modal">
        <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4" />
        <Modal.Body className="px-7 pb-7 pt-0 text-center">
          <div className="circle-56 bg-green-opacity-1 d-flex align-items-center justify-content-center mx-auto mb-5">
            <i className="fas fa-lock font-size-6 text-green" />
          </div>
          <h4 className="font-size-7 font-weight-bold text-black-2 mb-3">
            {isAuthenticated ? "Upgrade Required" : "Sign up to view"}
          </h4>
          <p className="font-size-4 text-muted mb-7 px-3">
            {isAuthenticated
              ? "This feature is not available on your current plan. Upgrade to unlock full access to all tools."
              : "Create a free account to access this section and explore plans when you are ready."}
          </p>
          {isAuthenticated ? (
            <Link
              to="/pricing"
              className="btn btn-green text-white font-weight-bold font-size-4 w-100 btn-h-50 upgrade-modal-btn"
              onClick={() => setShowUpgradeModal(false)}
            >
              View Plans
            </Link>
          ) : (
            <button
              type="button"
              className="btn btn-green text-white font-weight-bold font-size-4 w-100 btn-h-50 upgrade-modal-btn border-0"
              onClick={() => {
                setShowUpgradeModal(false);
                if (typeof gContext?.openAuthModalForSignUp === "function") {
                  gContext.openAuthModalForSignUp();
                }
              }}
            >
              Sign up to view
            </button>
          )}
          <button
            type="button"
            className="btn btn-link font-size-4 text-muted mt-3 w-100"
            onClick={() => setShowUpgradeModal(false)}
          >
            Maybe later
          </button>
        </Modal.Body>
      </Modal>
      </div>
    </div>
  );
};

export default SidebarDashboard2;

